import { Button, ButtonLevel, type ButtonProps, ButtonSize, Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { cx } from '@vizit/theme';
import { formatDate } from '@vizit/utils';
import * as React from 'react';

type IntegrationCardBaseProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> & {
  /**
   * The brand/integration icon displayed in the card header.
   *
   * @required
   * @type {React.ReactNode}
   */
  icon: React.ReactNode;
  /**
   * The name of the integration.
   *
   * @required
   * @type {string}
   */
  title: string;
  /**
   * A short description explaining what the integration does.
   *
   * @required
   * @type {string}
   */
  description: string;
  /**
   * Connected accounts rendered between the description and the footer. An integration can hold
   * several connections at once, so the card takes them as a slot rather than a single account.
   *
   * @optional
   * @type {React.ReactNode}
   */
  connections?: React.ReactNode;
};

type IntegrationCardDisconnectedProps = IntegrationCardBaseProps & {
  /**
   * Visual variant. Defaults to `'disconnected'` (the setup card).
   *
   * @optional
   * @default 'disconnected'
   */
  status?: 'disconnected';
  /**
   * The label displayed on the action button.
   *
   * @required
   * @type {string}
   */
  actionLabel: string;
  /**
   * Callback fired when the action button is pressed.
   *
   * @optional
   * @type {ButtonProps['onPress']}
   */
  onAction?: ButtonProps['onPress'];
  /**
   * Whether the action button is disabled.
   *
   * @optional
   * @default false
   * @type {boolean}
   */
  isActionDisabled?: boolean;
  /**
   * Whether the action button is in a loading state.
   *
   * @optional
   * @default false
   * @type {boolean}
   */
  isActionLoading?: boolean;
};

type IntegrationCardConnectedProps = IntegrationCardBaseProps & {
  /**
   * Visual variant. `'connected'` renders account details and a disconnect button.
   *
   * @required
   */
  status: 'connected';
  /**
   * Human-readable account/vendor name returned by the integration provider.
   *
   * @required
   * @type {string}
   */
  accountName: string;
  /**
   * Provider-side account identifier (e.g. vendor group id) shown for confirmation.
   *
   * @required
   * @type {string}
   */
  accountId: string;
  /**
   * Timestamp when the integration was connected. ISO string or Date.
   *
   * @required
   * @type {string | Date}
   */
  connectedAt: string | Date;
  /**
   * The label displayed on the disconnect button.
   *
   * @required
   * @type {string}
   */
  disconnectLabel: string;
  /**
   * Callback fired when the disconnect button is pressed.
   *
   * @optional
   * @type {ButtonProps['onPress']}
   */
  onDisconnect?: ButtonProps['onPress'];
  /**
   * Whether the disconnect button is in a loading state.
   *
   * @optional
   * @default false
   * @type {boolean}
   */
  isDisconnecting?: boolean;
};

type IntegrationCardProps = IntegrationCardDisconnectedProps | IntegrationCardConnectedProps;

/**
 * IntegrationCard displays a single retail/data integration the user can connect.
 * It shows the integration's branded icon, name, description and a primary action
 * button used to start the setup flow.
 *
 * @example
 * <IntegrationCard
 *   icon={<AmazonIcon size={20} />}
 *   title="Amazon Vendor Central"
 *   description="Sync your live ASINs, A+ Content, traffic and sales..."
 *   actionLabel="Set up integration"
 *   onAction={() => startSetup()}
 * />
 */
function IntegrationCard(props: IntegrationCardProps) {
  const {
    icon,
    title,
    description,
    connections,
    className,
    status,
    accountName,
    accountId,
    connectedAt,
    disconnectLabel,
    onDisconnect,
    isDisconnecting,
    actionLabel,
    onAction,
    isActionDisabled,
    isActionLoading,
    ...other
  } = props as Partial<IntegrationCardConnectedProps> &
    Partial<IntegrationCardDisconnectedProps> &
    IntegrationCardBaseProps;

  const isConnected = status === 'connected';
  const connectedAtLabel =
    isConnected && connectedAt
      ? formatDate(typeof connectedAt === 'string' ? new Date(connectedAt) : connectedAt)
      : null;

  return (
    <div
      className={cx('w-full bg-primary border border-secondary shadow-xs', 'flex flex-col gap-6', className)}
      {...other}
    >
      <div className="flex items-center gap-4 p-8">
        <div
          aria-hidden="true"
          className="flex items-center justify-center w-16 h-16 bg-primary border border-secondary shadow-xs shrink-0"
        >
          {icon}
        </div>
        <Text size={TextSize.Sm} weight={TextWeight.Medium} color={TextColor.Primary}>
          {title}
        </Text>
      </div>

      <Text elementType="p" size={TextSize.Sm} color={TextColor.Tertiary} showEllipsis={false} className="p-8">
        {description}
      </Text>

      {React.Children.count(connections) > 0 ? <div className="flex flex-col px-8">{connections}</div> : null}

      {isConnected && (
        <div className="flex flex-col gap-2 px-8">
          <Text size={TextSize.Sm} weight={TextWeight.Medium} color={TextColor.Primary}>
            {accountName}
          </Text>
          <Text size={TextSize.Xs} color={TextColor.Tertiary}>
            {accountId}
          </Text>
          <Text size={TextSize.Xs} color={TextColor.Tertiary}>
            Connected on {connectedAtLabel}
          </Text>
        </div>
      )}

      <div className="flex justify-end border-t border-secondary p-8">
        {isConnected ? (
          <Button
            size={ButtonSize.Md}
            level={ButtonLevel.Secondary}
            onPress={onDisconnect}
            isLoading={isDisconnecting ?? false}
          >
            {disconnectLabel}
          </Button>
        ) : (
          <Button
            size={ButtonSize.Md}
            level={ButtonLevel.Primary}
            onPress={onAction}
            isDisabled={isActionDisabled ?? false}
            isLoading={isActionLoading ?? false}
          >
            {actionLabel}
          </Button>
        )}
      </div>
    </div>
  );
}

export { IntegrationCard };
export type { IntegrationCardProps };
